import { datadogAddAction, getDatadogInstance } from '@cw-elements/analytics-provider';
import { analyticsReady } from 'cropwise_accounts/analytics';

/**
 * Client-side port of the SPA signup-campaign handling
 * (web-react-cropwise-accounts user-data.component.tsx). source_campaign arrives
 * base64-encoded as "campaignId::linkId"; we persist it so the campaign id and
 * staff link ride the Amplitude OAuth events (track_auth.js reads `campaignId`
 * from sessionStorage), and on mobile we flag the SIGNUP_CAMPAIGN user_origin on
 * Datadog RUM + localStorage exactly as the SPA does (NOT on the Amplitude event).
 */
const SOURCE_CAMPAIGN_SESSION_KEY = 'source_campaign_signup';
const CAMPAIGN_ID_KEY = 'campaignId';
const LINK_ID_KEY = 'linkId';
const USER_ORIGIN = 'SIGNUP_CAMPAIGN';

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Decoded source_campaign payload is "<campaignId>::<linkId>", optionally
// suffixed with "::<app marker>" (e.g. "::CropWise") by campaign links in
// the wild — the marker carries no id and is discarded.
const CAMPAIGN_PATTERN = /^(?<campaignId>[^:]*)::(?<linkId>[^:]*)(?:::.*)?$/;

export const decodeCampaign = (value) => {
  try {
    // atob decodes the base64 payload to an ASCII string ("id::id"); Uint8Array
    // .fromBase64 returns bytes and has limited browser support.
    return atob(value).match(CAMPAIGN_PATTERN)?.groups ?? {};
  } catch {
    return {};
  }
};

// Both calls run on page load, before the provider has finished starting: the RUM
// instance is still undefined and the action would be dropped without buffering,
// so they wait. Reaching the provider-owned instance also avoids importing
// @datadog/browser-rum here, which would load the SDK twice and detach the global
// context from the live session.
const reportMobileSignup = async ({ campaignId, linkId }) => {
  await analyticsReady;

  getDatadogInstance()?.setGlobalContextProperty('user_origin', USER_ORIGIN);
  datadogAddAction('Signup via Campaign on Mobile', {
    source: 'signup-campaign',
    // eslint-disable-next-line camelcase -- analytics property name
    user_origin: USER_ORIGIN,
    campaignId,
    linkId,
  });
};

export const initCampaignOrigin = () => {
  const params = new URLSearchParams(location.search);
  const sourceCampaign = params.get('source_campaign')
    || sessionStorage.getItem(SOURCE_CAMPAIGN_SESSION_KEY);
  if (!sourceCampaign || sourceCampaign === 'false') return;

  sessionStorage.setItem(SOURCE_CAMPAIGN_SESSION_KEY, sourceCampaign);
  const { campaignId, linkId } = decodeCampaign(sourceCampaign);
  if (campaignId) sessionStorage.setItem(CAMPAIGN_ID_KEY, campaignId);
  if (linkId) sessionStorage.setItem(LINK_ID_KEY, linkId);

  if (!isMobile()) return;

  localStorage.setItem('user_origin', USER_ORIGIN);

  void (async () => {
    try {
      await reportMobileSignup({ campaignId, linkId });
    } catch (error) {
      // Background failures must be reported without rejecting the event handler.
      console.error(error); // eslint-disable-line no-console
    }
  })();
};
